import { useEffect, type ReactNodeArray } from 'react';

import IssueListItem from '../issues/IssueListItem';
import ProjectChecksPassedPanel from './ProjectChecksPassedPanel';
import ProjectHasIssuesPanel from './ProjectHasIssuesPanel';
import ProjectPendingScanPanel from './ProjectPendingScanPanel';
import ProjectSummaryIssuesSummaryHeader from './ProjectSummaryIssuesSummaryHeader';
import type { ProjectDashboardProject, ProjectDashboardRevision, RevisionRouteParams } from '../projectDashboardTypes';
import * as IssuesV2Store from '../../../redux/issuesV2';
import { useDispatch, useSelector } from 'react-redux';
import { useQuery } from '../../../contexts/RouterContext';
import type { ProjectScope } from '../../../../shared/issuesV2';

export type ScanStatus = 'PENDING' | 'SUCCEEDED' | 'FAILED';

type Props = {
  project: ProjectDashboardProject;
  revision: ProjectDashboardRevision;
  params: RevisionRouteParams;
  routerPush(path: string): void;
  onScanIssues(ev): void;
};

const ProjectSummaryIssues = ({
  project,
  revision,
  params,
  routerPush,
  onScanIssues,
}: Props) => {
  const dispatch = useDispatch();
  const scope = useSelector(IssuesV2Store.selectors.getScope) as ProjectScope;
  const issueTypeSummary = useSelector(IssuesV2Store.selectors.getSortedIssueTypeSummary);
  const typeSummaryTotalIssueCount = useSelector(IssuesV2Store.selectors.getTotalIssuesFromTypeSummary);

  const [query] = useQuery<{ revisionScanId?: number }>((parserQuery) => ({
    revisionScanId: parserQuery.revision_scan_id ? Number(parserQuery.revision_scan_id) : undefined,
  }));

  // Fetch the summary of issues any time the branch or revision changes.
  // We can't rely solely on the Issues V2 scope because the Branch/Revision dropdowns aren't hooked up to Issues V2.
  // Once those are moved off of Reflux and can easily be hooked up into Issues V2, we can remove this.
  useEffect(() => {
    dispatch(IssuesV2Store.actions.fetchIssueTypeSummary({
      ...scope,
      revision: params.revision_id,
      revisionScanId: query.revisionScanId,
    }));
  }, [dispatch, scope, query.revisionScanId, params.revision_id]);

  // get the issue cards for the project summary panel
  let renderedIssues: ReactNodeArray;
  let issueCount = revision.data?.unresolved_issue_count || 0;
  const isScanned = revision.data?.meta?.[0]?.last_scan ?? false;

  // render issues cards
  // NOTE: this is messy and uses a combo of redux/reflux, however is necessary to support this page when `revisionScanId` is passed in the URI
  if (issueTypeSummary) {
    issueCount = typeSummaryTotalIssueCount || 0;
    renderedIssues = issueTypeSummary.map(([type, count]) => (
      <IssueListItem
        key={type}
        issueType={type}
        count={count}
        params={params}
        revision={revision.data}
        project={project.data}
        selectedRevisionScan={revision.data?.selectedRevisionScan}
      />
    ));
  } else if (revision.data) {
    const unresolvedIssueCounts = revision.data.unresolved_issue_counts || {};
    const issueTypeSummaryFromReflux = Object.entries(unresolvedIssueCounts)
      //  Remove counts of 0
      .filter(([, count]) => count > 0)
      //  Sort in descending order by count.
      .sort(([, countA], [, countB]) => countB - countA);

    renderedIssues = issueTypeSummaryFromReflux
      .map(([type, count]) => (
        <IssueListItem
          key={type}
          issueType={type}
          count={count}
          params={params}
          revision={revision.data}
          project={project.data}
          selectedRevisionScan={revision.data?.selectedRevisionScan}
      />
      ));
  }

  let projectScanStatus: ScanStatus;
  if (!isScanned) {
    projectScanStatus = 'PENDING';
  } else if (issueCount > 0) {
    projectScanStatus = 'FAILED';
  } else {
    projectScanStatus = 'SUCCEEDED';
  }

  return (
    <>
      <ProjectSummaryIssuesSummaryHeader
        params={params}
        revision={revision}
        project={project}
        issueCount={issueCount}
        status={projectScanStatus}
        routerPush={routerPush}
        onScanIssues={onScanIssues}
      />
      {projectScanStatus === 'PENDING' &&
        <ProjectPendingScanPanel
          params={params}
          revision={revision}
          project={project}
          onScanIssues={onScanIssues}
        />}
      {projectScanStatus !== 'PENDING'
        && (projectScanStatus === 'FAILED') &&
        <ProjectHasIssuesPanel
          renderedIssues={renderedIssues}
        />}
      {projectScanStatus === 'SUCCEEDED' &&
        <ProjectChecksPassedPanel
          params={params}
          revision={revision}
          project={project}
        />}
    </>
  );
};

export default ProjectSummaryIssues;
